import { useRef, useState } from "react";
import { useLive } from "../../state/use-live.js";
import { storage } from "../../state/storage.js";
import { useUiStore } from "../../state/ui-store.js";
import { gameModules } from "../../state/game-modules.js";
import {
  canExportGameCardSource,
  deleteGameCard,
  duplicateGameCard,
  exportGameCard,
  exportGameCardPackage,
  exportGameCardSource,
  getGameCardSourceExportState,
  importGameCard,
  liveStoredGameCardRecoveryState,
  openOfficialRqtSample,
  saveGameCard,
} from "../../state/game-cards.js";
import { GameCardEditor } from "./GameCardEditor.js";
import { GamePlayground, type GamePlaygroundTheme } from "./GamePlayground.js";
import { GameTemplateWizard } from "./GameTemplateWizard.js";
import { startGameSession } from "../../state/game-session.js";
import { useEscapeToClose } from "../use-escape-to-close.js";
import {
  compileGameDefinition,
  OFFICIAL_RQT_PLAYABLE_CARDS,
  OFFICIAL_RQT_SAMPLES,
  ROLEPLAY_MODULE_ID,
} from "@roqeet/core";
import { Compass, MessageCircle, Dices, VenetianMask, Key, Vote, Gamepad2, Map, ShieldCheck, Sparkles, Store, X } from "lucide-react";
import type { GameCard, GameDefinition, GameModule, OfficialRqtSample } from "@roqeet/core";
import { useT } from "../../i18n/use-t.js";

interface PlayingGame {
  module: GameModule;
  definition?: GameDefinition;
  theme?: GamePlaygroundTheme;
}

interface NoticeState {
  message: string;
  kind: "status" | "alert";
}

const playableStarterCardIds = new Set(OFFICIAL_RQT_PLAYABLE_CARDS.map((sample) => sample.id));

/**
 * 게임(모듈) 관리 화면 — 결정 #29 제작 사다리의 진입점. 두 묶음을 보여준다:
 *  1) 내가 만든 데이터형 게임 카드(편집/복제/삭제/활성토글 + 플레이).
 *  2) 기본 제공 네이티브 게임(읽기 전용 — 추상화 실증용 + 바로 플레이).
 *
 * 카드는 반응형 구독(결정 #13). 저장/삭제는 game-cards 액션이 레지스트리까지 갱신한다.
 */
export function GamesView() {
  const { t } = useT();
  const cards = useLive<GameCard[]>(() => storage.liveGameCards(), []);
  const recoveryState = useLive(() => liveStoredGameCardRecoveryState(), []);
  const setView = useUiStore((s) => s.setView);
  const setActiveChatId = useUiStore((s) => s.setActiveChatId);
  const [editing, setEditing] = useState<GameCard | "new" | null>(null);
  const [playing, setPlaying] = useState<PlayingGame | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<GameCard | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [toast, setToast] = useState<NoticeState | null>(null);
  const [recoveryNoticeDismissed, setRecoveryNoticeDismissed] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  useEscapeToClose(() => setDeleteTarget(null), !!deleteTarget && busyId !== deleteTarget?.id);
  const recommendedStarterCard = OFFICIAL_RQT_PLAYABLE_CARDS[0];

  // 카드/빌트인을 테스트 플레이그라운드로 연다(결정 #30). 데이터형 카드는 즉석
  // 컴파일, 빌트인은 등록 모듈을 그대로 쓴다. 컴파일 실패는 알림으로 표시.
  const testCard = (card: GameCard) => {
    try {
      setPlaying({
        module: compileGameDefinition(card.definition),
        definition: card.definition,
        theme: themeForGameCard(card),
      });
    } catch (e) {
      alert(t("game.compileError", { error: e instanceof Error ? e.message : String(e) }));
    }
  };
  const testModule = (module: GameModule) => setPlaying({ module });

  // 실제 세션을 시작하고 채팅방을 연다(결정 #25/#28). 데이터형 카드는 즉석 컴파일한
  // 모듈로, 빌트인은 등록 모듈로 startGameSession에 위임한다(ChatsView와 공유).
  const playCard = async (card: GameCard) => {
    try {
      const chatId = await startGameSession(compileGameDefinition(card.definition), (k, ...args) => t(k as any, ...args));
      setActiveChatId(chatId);
      setView("chats");
    } catch (e) {
      setNotice({ message: t("game.playStartFailed", { error: e instanceof Error ? e.message : String(e) }), kind: "alert" });
    }
  };
  const playModule = async (module: GameModule) => {
    const chatId = await startGameSession(module, (k, ...args) => t(k as any, ...args));
    setActiveChatId(chatId);
    setView("chats");
  };

  // 게임 카드 가져오기(결정 #6/#29). core가 id를 새로 부여하므로 충돌 없음.
  const onImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setNotice(null);
    setToast(null);
    try {
      const card = await importGameCard(file);
      setNotice({ message: t("game.imported", { title: card.definition.title }), kind: "status" });
    } catch (err) {
      setNotice({ message: t("game.importFailed", { error: err instanceof Error ? err.message : String(err) }), kind: "alert" });
    }
  };

  // 기본 제공 모듈: 사용자 카드(저장된 GameCard)가 아닌 등록 모듈 = 빌트인.
  // 롤플레잉(자유대화)은 게임 목록에서 제외. 네이티브든 빌트인 데이터형이든 모두 노출.
  const cardModuleIds = new Set((cards ?? []).map((c) => c.definition.id));
  const builtins = gameModules
    .list()
    .filter((m) => m.manifest.id !== ROLEPLAY_MODULE_ID && !cardModuleIds.has(m.manifest.id));

  const toggleEnabled = async (card: GameCard) => {
    setBusyId(card.id);
    try {
      await saveGameCard({ ...card, enabled: !card.enabled });
    } finally {
      setBusyId(null);
    }
  };

  const tryOfficialSample = async (sampleId: string) => {
    setBusyId(sampleId);
    setNotice(null);
    setToast(null);
    try {
      const card = await openOfficialRqtSample(sampleId);
      setToast({ message: t("game.sampleOpened", { title: card.definition.title }), kind: "status" });
      testCard(card);
    } catch (e) {
      setNotice({ message: t("game.sampleOpenFailed", { error: e instanceof Error ? e.message : String(e) }), kind: "alert" });
    } finally {
      setBusyId(null);
    }
  };

  const duplicate = async (card: GameCard) => {
    // 복제는 새 id로 저장(definition.id도 팩토리가 새로 시드) — 충돌 없음.
    await duplicateGameCard(card, t("game.copyTitle", { title: card.definition.title }));
  };

  const requestDelete = (card: GameCard) => {
    setDeleteTarget(card);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const card = deleteTarget;
    setBusyId(card.id);
    setNotice(null);
    setToast(null);
    try {
      await deleteGameCard(card);
      setDeleteTarget(null);
    } catch (e) {
      setNotice({ message: e instanceof Error ? e.message : String(e), kind: "alert" });
    } finally {
      setBusyId(null);
    }
  };

  // 모듈로 새 세션을 열고 채팅으로 이동(ChatsView의 모듈 선택과 동일 경로를 재사용하기
  // 위해, 여기서는 간단히 대화 화면으로 보내고 거기서 "새 대화 → 모듈 선택"을 안내).
  const tryExportCard = (card: GameCard, format: ".rqt" | ".rqg" | ".rqp") => {
    setNotice(null);
    setToast(null);
    try {
      if (format === ".rqt") {
        if (!exportGameCardSource(card)) {
          throw new Error(t("game.sourceRqtLegacy"));
        }
      } else if (format === ".rqg") {
        exportGameCard(card);
      } else {
        exportGameCardPackage(card);
      }
    } catch (e) {
      setNotice({ message: `Export failed (${format}): ${e instanceof Error ? e.message : String(e)}`, kind: "alert" });
    }
  };

  const showRecoveryNotice =
    !recoveryNoticeDismissed &&
    !!recoveryState &&
    (recoveryState.invalidCount > 0 || recoveryState.repairedCount > 0);

  const playHint = () => {
    setView("chats");
    setActiveChatId(null);
  };

  return (
    <div className="flex h-full flex-col">
      {editing && <GameCardEditor card={editing} onClose={() => setEditing(null)} />}
      {playing && (
        <GamePlayground
          module={playing.module}
          {...(playing.definition ? { definition: playing.definition } : {})}
          {...(playing.theme ? { theme: playing.theme } : {})}
          onClose={() => setPlaying(null)}
        />
      )}
      {wizardOpen && (
        <GameTemplateWizard
          onClose={() => setWizardOpen(false)}
          onCreated={(card) => setEditing(card)}
        />
      )}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3"
          role="dialog"
          aria-modal="true"
        >
          <div className="max-h-[calc(100vh-1.5rem)] max-h-[calc(100dvh-1.5rem)] w-full max-w-sm overflow-y-auto rounded-2xl border border-line bg-bg p-4 shadow-xl scroll-touch">
            <h2 className="text-sm font-semibold text-ink">{t("common.delete")}</h2>
            <p className="mt-2 text-sm text-ink-soft">
              {t("game.deleteConfirm", { title: deleteTarget.definition.title })}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="min-h-10 touch-manipulation rounded-lg border border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised disabled:opacity-50"
                onClick={() => setDeleteTarget(null)}
                disabled={busyId === deleteTarget.id}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="min-h-10 touch-manipulation rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
                onClick={() => void confirmDelete()}
                disabled={busyId === deleteTarget.id}
              >
                {t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="application/json,text/plain,application/zip,application/octet-stream,.rqt,.rqt.txt,.rqg,.rqg.json,.rqp,.rqp.zip,.zip,.json"
        className="hidden"
        onChange={onImport}
      />

      <div className="flex items-center justify-between gap-2 border-b border-line p-3">
        <span className="text-sm text-ink-soft">
          {cards ? t("game.count", { count: cards.length }) : t("common.loading")}
        </span>
        <div className="flex flex-wrap justify-end gap-2">
          <button
            className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={() => fileRef.current?.click()}
            title={t("game.importJsonTitle")}
          >
            {t("common.import")}
          </button>
          <button
            className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={() => setWizardOpen(true)}
            title={t("game.templateTitle")}
          >
            {t("game.template")}
          </button>
          <button
            className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft"
            onClick={() => setEditing("new")}
          >
            {t("game.new")}
          </button>
        </div>
      </div>

      {toast && (
        <div
          className="fixed right-3 top-16 z-[60] flex w-[min(24rem,calc(100vw-1.5rem))] items-start justify-between gap-2 rounded-lg border border-line bg-bg px-3 py-2 text-xs text-ink-soft shadow-xl"
          role={toast.kind}
          aria-live={toast.kind === "alert" ? "assertive" : "polite"}
          aria-atomic="true"
          data-notice-surface="toast"
        >
          <span className="min-w-0 break-words">{toast.message}</span>
          <button
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-ink-faint hover:bg-bg-raised hover:text-ink"
            onClick={() => setToast(null)}
            aria-label={t("misc.close")}
            title={t("misc.close")}
          >
            <X size={15} aria-hidden="true" />
          </button>
        </div>
      )}

      {notice && (
        <div
          className="flex items-start justify-between gap-2 border-b border-line bg-bg-soft px-3 py-2 text-xs text-ink-soft"
          role={notice.kind}
          aria-live={notice.kind === "alert" ? "assertive" : "polite"}
          aria-atomic="true"
          data-notice-surface="inline"
        >
          <span className="min-w-0 break-words">{notice.message}</span>
          <button
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-ink-faint hover:bg-bg-raised hover:text-ink"
            onClick={() => setNotice(null)}
            aria-label={t("misc.close")}
            title={t("misc.close")}
          >
            <X size={15} aria-hidden="true" />
          </button>
        </div>
      )}

      {showRecoveryNotice && (
        <div
          className="flex items-start justify-between gap-2 border-b border-line bg-bg-soft px-3 py-2 text-xs text-ink-soft"
          role="status"
          aria-live="polite"
          data-notice-surface="inline"
          aria-atomic="true"
        >
          <span className="min-w-0 break-words">{t("game.damagedCardsSkipped")}</span>
          <button
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-ink-faint hover:bg-bg-raised hover:text-ink"
            onClick={() => setRecoveryNoticeDismissed(true)}
            aria-label={t("misc.close")}
            title={t("misc.close")}
          >
            <X size={15} aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-3 scroll-touch">
        {cards && cards.length === 0 && recommendedStarterCard && (
          <section className="mb-6 rounded-xl border border-accent/30 bg-bg-soft p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-accent-soft">
              {t("game.firstRunEyebrow")}
            </p>
            <h2 className="mt-1 text-base font-semibold text-ink">
              {t("game.firstRunTitle")}
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-ink-soft">
              {t("game.firstRunDesc")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="min-h-9 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
                onClick={() => void tryOfficialSample(recommendedStarterCard.id)}
                disabled={busyId === recommendedStarterCard.id}
              >
                {t("game.firstRunTryStarter", { title: recommendedStarterCard.title })}
              </button>
              <a
                className="min-h-9 rounded-lg border border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised"
                href="#official-starter-cards"
              >
                {t("game.firstRunBrowse")}
              </a>
              <button
                type="button"
                className="min-h-9 rounded-lg border border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised"
                onClick={() => fileRef.current?.click()}
              >
                {t("game.firstRunImport")}
              </button>
            </div>
            <p className="mt-3 text-xs text-ink-faint">
              {t("game.localFirstShortNote")}
            </p>
          </section>
        )}

        <section className="mb-6 rounded-xl border border-line bg-bg-soft px-3 py-2">
          <p className="text-xs font-medium text-ink-soft">
            {t("game.localFirstTitle")}
          </p>
          <p className="mt-1 text-xs text-ink-faint">
            {t("game.localFirstExportNote")}
          </p>
        </section>

        <section id="official-samples" className="mb-6 scroll-mt-4">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {t("game.officialSamples")}
          </h2>
          <ul className="grid gap-2 xl:grid-cols-2">
            {OFFICIAL_RQT_SAMPLES.map((sample) => officialRqtCardItem(sample, busyId, tryOfficialSample, t))}
          </ul>
        </section>
        <section id="official-starter-cards" className="mb-6 scroll-mt-4">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {t("game.playableStarterCards")}
          </h2>
          <ul className="grid gap-2 xl:grid-cols-2">
            {OFFICIAL_RQT_PLAYABLE_CARDS.map((sample) => officialRqtCardItem(sample, busyId, tryOfficialSample, t))}
          </ul>
        </section>
        {/* 내가 만든 데이터형 게임 */}
        <section className="mb-6">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {t("game.myGames")}
          </h2>
          {cards && cards.length === 0 && (
            <p className="rounded-xl border border-dashed border-line px-3 py-4 text-center text-sm text-ink-faint">
              {t("game.emptyMine")}
            </p>
          )}
          <ul className="space-y-2">
            {(cards ?? []).map((card) => (
              <li
                key={card.id}
                className="flex min-w-0 flex-col gap-2 rounded-xl border border-line bg-bg-soft px-3 py-3 sm:flex-row sm:items-start"
              >
                <span className="mt-0.5" aria-hidden>
                  {iconFor(card.definition.icon)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <span className="min-w-0 break-words text-sm font-medium text-ink">
                      {card.definition.title}
                    </span>
                    {!card.enabled && (
                      <span className="rounded bg-bg-raised px-1.5 py-0.5 text-[10px] text-ink-faint">
                        {t("game.inactive")}
                      </span>
                    )}
                    {sampleCardBadge(card, t) && (
                      <span className="rounded bg-bg-raised px-1.5 py-0.5 text-[10px] text-ink-faint">
                        {sampleCardBadge(card, t)}
                      </span>
                    )}
                  </div>
                  {card.definition.description && (
                    <p className="mt-0.5 line-clamp-2 text-xs text-ink-faint">
                      {card.definition.description}
                    </p>
                  )}
                  {sourceFreshnessLabel(card, t) && (
                    <p className="mt-1 break-words text-[11px] text-ink-faint">
                      {sourceFreshnessLabel(card, t)}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                    <ActionBtn onClick={() => playCard(card)} accent>
                      {t("game.play")}
                    </ActionBtn>
                    <ActionBtn onClick={() => testCard(card)}>{t("game.test")}</ActionBtn>
                    <ActionBtn onClick={() => setEditing(card)}>{t("common.edit")}</ActionBtn>
                    <ActionBtn onClick={() => duplicate(card)}>{t("game.duplicate")}</ActionBtn>
                    {canExportGameCardSource(card) && (
                      <ActionBtn onClick={() => tryExportCard(card, ".rqt")} title={t("game.sourceRqtCurrent")}>
                        .rqt
                      </ActionBtn>
                    )}
                    <ActionBtn onClick={() => tryExportCard(card, ".rqg")} title={t("game.exportRqgTitle")}>
                      .rqg
                    </ActionBtn>
                    <ActionBtn onClick={() => tryExportCard(card, ".rqp")}>.rqp</ActionBtn>
                    <ActionBtn onClick={() => toggleEnabled(card)} disabled={busyId === card.id}>
                      {card.enabled ? t("game.disable") : t("game.enable")}
                    </ActionBtn>
                    <ActionBtn onClick={() => requestDelete(card)} danger>
                      {t("common.delete")}
                    </ActionBtn>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* 기본 제공 네이티브 게임(읽기 전용) */}
        {builtins.length > 0 && (
          <section>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
              {t("game.builtins")}
            </h2>
            <ul className="space-y-2">
              {builtins.map((m) => (
                <li
                  key={m.manifest.id}
                  className="flex items-start gap-3 rounded-xl border border-line px-3 py-2"
                >
                  <span className="mt-0.5" aria-hidden>
                    {iconFor(m.manifest.icon)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="min-w-0 break-words text-sm font-medium text-ink">{m.manifest.titleKey ? t(m.manifest.titleKey as any) : m.manifest.title}</span>
                    {m.manifest.description && (
                      <p className="mt-0.5 text-xs text-ink-faint">{m.manifest.descriptionKey ? t(m.manifest.descriptionKey as any) : m.manifest.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                      <ActionBtn onClick={() => playModule(m)} accent>
                        {t("game.play")}
                      </ActionBtn>
                      <ActionBtn onClick={() => testModule(m)}>{t("game.test")}</ActionBtn>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <p className="mt-6 text-center text-xs text-ink-faint">
          {t("game.playHintBefore")}{" "}
          <button className="text-accent-soft underline" onClick={playHint}>
            {t("game.playHintNewChat")}
          </button>
          {t("game.playHintAfter")}
        </p>
      </div>
    </div>
  );
}

function themeForGameCard(card: GameCard): GamePlaygroundTheme | undefined {
  const officialRqtSampleId = card.extensions?.["officialRqtSampleId"];
  const basedOnOfficialRqtSampleId = card.extensions?.["basedOnOfficialRqtSampleId"];
  const officialSampleId =
    typeof officialRqtSampleId === "string"
      ? officialRqtSampleId
      : typeof basedOnOfficialRqtSampleId === "string"
        ? basedOnOfficialRqtSampleId
        : undefined;
  return officialSampleId && playableStarterCardIds.has(officialSampleId) ? "gamebook" : undefined;
}

function officialRqtCardItem(
  sample: OfficialRqtSample,
  busyId: string | null,
  tryOfficialSample: (sampleId: string) => Promise<void>,
  t: ReturnType<typeof useT>["t"],
): React.ReactNode {
  const isStarterCard = sample.kind === "playable-card";
  return (
    <li
      key={sample.id}
      className={[
        "flex min-w-0 flex-col gap-2 rounded-xl border px-3 py-3 sm:flex-row sm:items-start",
        isStarterCard ? "border-accent/30 bg-bg-soft shadow-sm" : "border-line bg-bg-soft",
      ].join(" ")}
    >
      <span className="mt-0.5" aria-hidden>
        {iconFor(sampleIcon(sample.category))}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="min-w-0 break-words text-sm font-medium text-ink">{sample.title}</span>
          <span className="rounded bg-bg-raised px-1.5 py-0.5 text-[10px] text-ink-faint">
            {isStarterCard ? "Starter card" : "Tutorial"}
          </span>
          <span className="rounded bg-bg-raised px-1.5 py-0.5 text-[10px] text-ink-faint">
            {sampleCategoryLabel(sample.category)}
          </span>
          <span className="rounded bg-bg-raised px-1.5 py-0.5 text-[10px] text-ink-faint">
            {sampleDifficultyLabel(sample.difficulty)}
          </span>
          {sample.playLength && (
            <span className="rounded bg-bg-raised px-1.5 py-0.5 text-[10px] text-ink-faint">
              {sample.playLength}
            </span>
          )}
          {sample.recommendedTheme === "gamebook" && (
            <span className="rounded bg-accent/10 px-1.5 py-0.5 text-[10px] text-accent-soft">
              Gamebook recommended
            </span>
          )}
        </div>
        <p className="mt-0.5 line-clamp-2 text-xs text-ink-faint">
          {sample.description}
        </p>
        {sample.showcases?.length ? (
          <p className="mt-1 break-words text-[11px] text-ink-faint">
            Demonstrates: {sample.showcases.join(", ")}
          </p>
        ) : null}
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
          <ActionBtn
            onClick={() => void tryOfficialSample(sample.id)}
            disabled={busyId === sample.id}
            accent
          >
            {t("game.trySample")}
          </ActionBtn>
          <span className="text-[11px] text-ink-faint">.rqt -&gt; .rqg</span>
        </div>
      </div>
    </li>
  );
}

function ActionBtn({
  onClick,
  danger,
  accent,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  danger?: boolean;
  accent?: boolean;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={[
        "min-h-8 max-w-full touch-manipulation rounded px-2.5 py-1 text-left leading-tight hover:bg-bg-raised disabled:opacity-50",
        danger ? "text-red-400" : accent ? "font-medium text-accent-soft" : "text-ink-soft",
      ].join(" ")}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

/** 아이콘 키를 SVG 아이콘으로(ChatsView 매핑과 동일 키 집합 + game 기본). */
function sampleCardBadge(card: GameCard, t: ReturnType<typeof useT>["t"]): string | undefined {
  if (card.extensions?.["sourceKind"] === "official-sample") return t("game.officialSampleBadge");
  if (typeof card.extensions?.["basedOnOfficialRqtSampleId"] === "string") return t("game.basedOnOfficialSampleBadge");
  return undefined;
}

function sourceFreshnessLabel(card: GameCard, t: ReturnType<typeof useT>["t"]): string | undefined {
  switch (getGameCardSourceExportState(card)) {
    case "current":
      return t("game.sourceRqtCurrent");
    case "stale":
      return t("game.sourceRqtStale");
    case "legacy":
      return t("game.sourceRqtLegacy");
    default:
      return undefined;
  }
}

function iconFor(icon: string | undefined, className = "w-5 h-5 text-accent"): React.ReactNode {
  switch (icon) {
    case "compass": return <Compass className={className} />;
    case "chat": return <MessageCircle className={className} />;
    case "dice": return <Dices className={className} />;
    case "mask": return <VenetianMask className={className} />;
    case "key": return <Key className={className} />;
    case "vote": return <Vote className={className} />;
    case "map": return <Map className={className} />;
    case "sparkles": return <Sparkles className={className} />;
    case "store": return <Store className={className} />;
    case "shield": return <ShieldCheck className={className} />;
    default: return <Gamepad2 className={className} />;
  }
}

function sampleIcon(category: string): string {
  switch (category) {
    case "escape": return "key";
    case "character": return "sparkles";
    case "mystery": return "compass";
    case "exploration": return "map";
    case "cyoa": return "map";
    case "survival": return "shield";
    case "school": return "sparkles";
    case "wuxia": return "compass";
    default: return "game";
  }
}

function sampleCategoryLabel(category: string): string {
  switch (category) {
    case "cyoa": return "CYOA";
    case "school": return "Campus";
    case "wuxia": return "Wuxia";
    default: return category.charAt(0).toUpperCase() + category.slice(1);
  }
}

function sampleDifficultyLabel(difficulty: string): string {
  return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
}
